import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { AdditionalGuestInput, NationalityType } from '@/models/self-checkin/accommodation-info'
import { FrontTabletSmallCheckbox } from '@/components/molecules/front-tablet/input/small-checkbox'
import { PaxSelect } from '@/components/molecules/pax-select'
import { countryList } from '@/libs/nationality'
import { FrontTabletFormLabel } from '@/components/molecules/front-tablet/input/form-label'
import { FrontTabletInputField } from '@/components/molecules/front-tablet/input/input-field'
import { ErrorMessage } from '@/components/molecules/front-tablet/input/error-message'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'

type FormProps = {
  additionalGuestInfo: AdditionalGuestInput
  onChange: (name: any, value: any) => void
  requiredAccompanyNameInput: boolean
  isFormValidRequiredFields: boolean
  imageInfo?: {
    identificationFrontImage: string | undefined
    identificationBackImage: string | undefined
    representativePassportImage: string | undefined
    nationality: string | undefined
    setNationality: (v: string | undefined) => void
    uploadImage: (type: 'front' | 'back' | 'representativePassport') => void
  }
  isFirstNameValid?: any
  isLastNameValid?: any
}

export const AdditionalGuestForm: React.FC<FormProps> = ({
  additionalGuestInfo,
  onChange,
  requiredAccompanyNameInput,
  isFormValidRequiredFields,
  imageInfo,
  isFirstNameValid,
  isLastNameValid,
}) => {
  const { t, i18n } = useTranslation()
  const nationalities = countryList(i18n.language, false)

  const onChangeAdditionalGuestInfo = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target
    onChange(name, value)
  }

  const getName = (lastName, firstName) => {
    switch (true) {
      case !!lastName?.trim() && !!firstName?.trim():
        return lastName + ' ' + firstName
      case !!lastName?.trim():
        return lastName
      case !!firstName?.trim():
        return firstName
      default:
        return ''
    }
  }

  const onChangeAdditionalGuestInfoPax = (value: string): void => {
    onChange('paxTotal', value)
    if (value === '1') {
      onChange('accompany', [])
    } else {
      const selectAccompanyLength = Number(value) - 1
      const stateAccompany = additionalGuestInfo?.accompany || []
      const diffAccompanyLength = selectAccompanyLength - stateAccompany.length
      if (selectAccompanyLength < 0 || diffAccompanyLength === 0) {
        return
      }
      if (diffAccompanyLength < 0) {
        const reduceAccompany = stateAccompany.slice(0, diffAccompanyLength)
        onChange('accompany', reduceAccompany)
      } else {
        const addAccompany = Array.from(new Array(diffAccompanyLength)).map(() => {
          return { id: uuidv4(), lastName: '', firstName: '', name: '' }
        })
        onChange('accompany', [...stateAccompany, ...addAccompany])
      }
    }
  }

  const onChangeAdditionalGuestInfoAccompanyName = (e, id): void => {
    const { name, value } = e.target
    const stateAccompany = additionalGuestInfo?.accompany || []
    const changeAccompany = stateAccompany.map(accompany => {
      if (accompany.id === id) {
        const changeLastNameValue = name === 'lastName' ? value : accompany.lastName
        const changeFirstNameValue = name === 'firstName' ? value : accompany.firstName
        const changeNameValue = getName(changeLastNameValue, changeFirstNameValue)
        return {
          id,
          lastName: changeLastNameValue,
          firstName: changeFirstNameValue,
          name: changeNameValue,
        }
      } else {
        return accompany
      }
    })
    onChange('accompany', changeAccompany)
  }

  return (
    <div css={form}>
      {imageInfo && (
        <>
          <p css={formDescriptionStyle}>{t('Representative information')}</p>
          <div css={itemWrapperStyle}>
            {imageInfo.identificationFrontImage && (
              <>
                <FrontTabletFormLabel label={t('Identification')} required />
                <div css={imageItemWrapperStyle}>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('front')}>
                    <img
                      src={
                        imageInfo?.identificationFrontImage
                          ? imageInfo?.identificationFrontImage
                          : require('@/static/images/new_identification_card_front.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Front of the card')} />
                        <p css={scan}>{t('Front of the card')}</p>
                      </div>
                    </div>
                  </div>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('back')}>
                    <img
                      src={
                        imageInfo?.identificationBackImage
                          ? imageInfo?.identificationBackImage
                          : require('@/static/images/new_identification_card_back.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Back of the card')} />
                        <p css={scan}>{t('Back of the card')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {imageInfo.representativePassportImage && (
              <>
                <FrontTabletFormLabel label={t('Identification')} required />
                <div css={imageItemWrapperStyle}>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('representativePassport')}>
                    <img
                      src={
                        imageInfo?.representativePassportImage
                          ? imageInfo?.representativePassportImage
                          : require('@/static/images/passport.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Take a Photo')} />
                        <p css={scan}>{t('Take a Photo')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Residence')} required />
        <div css={radioGroupContainerStyle}>
          {[
            { value: NationalityType.Jp, label: t('Japanese nationality') },
            { value: NationalityType.Njp, label: t('Foreign nationality') },
          ].map((item, index) => (
            <FrontTabletRadioButton
              key={item.value}
              id={`nationality-${item.value}`}
              label={item.label}
              formValue={additionalGuestInfo?.nationality as NationalityType}
              name="nationality"
              onChange={e => onChange('nationality', e.target.value as NationalityType)}
              value={item.value}
              radioSizeType="small"
              radioLabelSizeType="small"
              itemCss={getRadioItemStyle(index === 0)}
            />
          ))}
        </div>
      </div>

      {imageInfo?.representativePassportImage && (
        <div className="reservationLabel">
          <FrontTabletFormLabel label={t('Nationality')} />
          <div css={accompanyNameForm}>
            <select
              css={[assignSelectStyle, css({ width: 200 })]}
              value={imageInfo.nationality}
              onChange={e => imageInfo.setNationality(e.target.value)}
            >
              <option value={''}>{t('Country name')}</option>
              {nationalities.map((country, index) => (
                <option value={country.value} key={index}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Full name')} required />
        <div css={nameForm}>
          <FrontTabletInputField
            type="text"
            className="lastName"
            placeholder={t('Last name')}
            name="lastName"
            value={additionalGuestInfo?.lastName}
            onChange={onChangeAdditionalGuestInfo}
            error={(!isFormValidRequiredFields && !additionalGuestInfo?.lastName.trim()) || !isLastNameValid}
          />
          <FrontTabletInputField
            type="text"
            placeholder={t('First name')}
            name="firstName"
            value={additionalGuestInfo?.firstName}
            error={(!isFormValidRequiredFields && !additionalGuestInfo?.firstName.trim()) || !isFirstNameValid}
            onChange={onChangeAdditionalGuestInfo}
          />
        </div>
        {!isFormValidRequiredFields && (!additionalGuestInfo?.lastName.trim() || !additionalGuestInfo?.firstName.trim()) ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
        {!isLastNameValid || !isFirstNameValid ? <ErrorMessage>{t('Please type using half-width characters')}</ErrorMessage> : null}
      </div>

      <div className="reservationLabel" style={{ borderTop: '1px solid #F2F2F2', marginTop: 24, paddingTop: 40, paddingBottom: 0 }}>
        <FrontTabletFormLabel label={t('Number of guests')} />
        <div css={accompanyPaxForm}>
          <PaxSelect value={`${additionalGuestInfo?.paxTotal || 1}`} onClick={val => onChangeAdditionalGuestInfoPax(val)}></PaxSelect>
        </div>
        {(additionalGuestInfo?.paxTotal || 1) > 1 && (
          <>
            {!requiredAccompanyNameInput && (
              <div css={checkBoxStyle}>
                <FrontTabletSmallCheckbox
                  value={additionalGuestInfo.hasContactForAllGuest}
                  onChange={val => {
                    onChange('hasContactForAllGuest', val)
                    if (!val) {
                      onChangeAdditionalGuestInfoPax(`${additionalGuestInfo?.paxTotal || 1}`)
                    }
                  }}
                  label={t('has contact information for all guests')}
                />
              </div>
            )}
            <div id="accompanyNameForm">
              {(requiredAccompanyNameInput || !additionalGuestInfo.hasContactForAllGuest) && (
                <div className="reservationLabel">
                  <div css={accompanyNameForm}>
                    <FrontTabletFormLabel label={t('Accompany name')} required />
                    {(additionalGuestInfo?.accompany || []).map(accompany => (
                      <div key={accompany?.id} css={nameFormList}>
                        <div css={nameForm}>
                          <FrontTabletInputField
                            type="text"
                            className="lastName"
                            placeholder={t('Last name')}
                            name="lastName"
                            value={accompany?.lastName}
                            error={!isFormValidRequiredFields && !accompany?.lastName?.trim()}
                            onChange={e => onChangeAdditionalGuestInfoAccompanyName(e, accompany?.id)}
                          />
                          <FrontTabletInputField
                            type="text"
                            placeholder={t('First name')}
                            name="firstName"
                            value={accompany?.firstName}
                            error={!isFormValidRequiredFields && !accompany?.firstName?.trim()}
                            onChange={e => onChangeAdditionalGuestInfoAccompanyName(e, accompany?.id)}
                          />
                        </div>
                        {!isFormValidRequiredFields && (!accompany?.lastName?.trim() || !accompany?.firstName?.trim()) ? (
                          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const form = css({
  width: '100%',
  maxWidth: 'none',

  '.reservationLabel': {
    width: '100%',
    padding: '16px 0',
  },
})

const radioGroupContainerStyle = css({
  display: 'flex',
  gap: 16,
  marginTop: 16,
})

const getRadioItemStyle = (isFirst: boolean) =>
  css({
    display: 'flex',
    lineHeight: 'initial',
    ...(isFirst && { marginRight: 50 }),
  })

const nameForm = css(
  {
    display: 'flex',
    marginTop: '16px',

    '.lastName': {
      marginRight: '20px',
    },
  },
  // createSelfCheckinResponsiveStyle({}, { flexWrap: 'wrap', gap: 20, '.lastName': { marginRight: 0 } }),
)

const assignSelectStyle = css(
  {
    width: '100%',
    height: '61px',
    border: '1px solid #cccccc',
    color: '#272727',
    borderRadius: '40px',
    fontSize: '22px',
    padding: '14px 35px',
    appearance: 'none',
    backgroundImage: `url(${require('@/static/images/arrow_down_yellow.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '95% center',
    backgroundSize: 14,
    backgroundColor: '#FFFFFF',
    display: 'block',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      fontSize: FrontTabletResponsiveToken.formInputFontSize.landscape13,
    },
  }),
)

const formDescriptionStyle = css(
  {
    fontSize: FrontTabletResponsiveToken.guestFormTitleFontSize.landscape11,
    marginBottom: 24,
    lineHeight: '27px',
    letterSpacing: '1.8px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: FrontTabletResponsiveToken.guestFormTitleFontSize.landscape13, lineHeight: '36px' },
  }),
)

const itemWrapperStyle = css({
  width: '100%',
  '&:last-child': {
    padding: 0,
  },
})

const imageItemWrapperStyle = css(
  {
    display: 'flex',
    marginTop: 16,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { flexWrap: 'wrap' },
  }),
)

const imageItemStyle = css(
  {
    width: 256,
    height: 158,
    cursor: 'pointer',
    position: 'relative',
    fontSize: 18,
    fontWeight: 'bold',
    letterSpacing: '2.7px',
    lineHeight: '27px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:nth-of-type(1)': {
      margin: '0 20px 20px 0',
    },
    '> img': {
      width: 256,
      height: 158,
      borderRadius: 10,
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      width: 296,
      height: 183,
      fontSize: 22,
      '> img': { width: 296, height: 183 },
    },
  }),
)

const imageItemTextStyle = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  margin: 'auto',
  display: 'flex',
  fontSize: 12,
  color: '#ffffff',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  img: {
    width: 19,
    height: 'auto',
  },
  p: {
    color: '#ffffff',
  },
})

const scan = css({
  fontSize: '15px !important',
})

const accompanyPaxForm = css({
  marginTop: '16px',
  width: 'fit-content',
  '> div > div > span': {
    fontSize: '22px !important',
  },
})

const accompanyNameForm = css({
  marginTop: 24,
})

const nameFormList = css({
  marginBottom: 30,
})

const checkBoxStyle = css({
  padding: '24px 32px 0 0',
})
